import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useState } from 'react';
import { Button, Box } from '@mui/material';
import { Editor } from '@tiptap/react';
import { useEditorBubbleMenu } from 'components/EditorBubbleMenu/context';
import { ARTICLE_BODY_COPY_ID } from 'screens/editorial/asset/utility/getEditorConfigs';
import LinkPreview from './LinkPreview';
import LinkAssetSearch from './LinkAssetSearch';
import LinkHeadingsList from './LinkHeadingsList';
import BaseBubble from '../BaseBubble';

const VIEW_TYPES = {
  SEARCH: 'search',
  HEADINGS: 'headings',
} as const;

type ViewType = (typeof VIEW_TYPES)[keyof typeof VIEW_TYPES];

type ViewToggleProps = {
  editor: Editor;
  view: ViewType;
  onToggle: () => void;
};

const ViewToggleButton = ({ editor, view, onToggle }: ViewToggleProps) => {
  const canShowHeadings =
    editor?.options?.element instanceof Element &&
    editor?.options?.element?.id === ARTICLE_BODY_COPY_ID;

  if (!editor || !canShowHeadings) {
    return null;
  }

  return (
    <Button
      data-testid="bubble-menu-link-toggle-view-btn"
      onClick={onToggle}
      sx={{
        width: '100%',
        gap: 0.5,
        color: 'text.secondary',
        mb: view === VIEW_TYPES.HEADINGS ? 2 : 0,
        justifyContent: view === VIEW_TYPES.HEADINGS ? 'flex-end' : 'space-between',
        flexDirection: view === VIEW_TYPES.HEADINGS ? 'row-reverse' : 'row',
      }}
    >
      <Box data-testid="bubble-menu-link-toggle-view-btn-label">
        {view === VIEW_TYPES.HEADINGS ? 'Search' : 'Headings'}
      </Box>
      <ArrowForwardIcon
        sx={{
          fontSize: '1rem',
          transform: `rotate(${view === VIEW_TYPES.HEADINGS ? 180 : 0}deg)`,
        }}
      />
    </Button>
  );
};

export const LinkBubble = () => {
  const { editor, bubbleViewState } = useEditorBubbleMenu();
  const [view, setView] = useState<ViewType>(VIEW_TYPES.SEARCH);
  const isPreview =
    typeof bubbleViewState === 'string' && bubbleViewState.startsWith('linkPreview');

  const toggleView = () =>
    setView(view === VIEW_TYPES.HEADINGS ? VIEW_TYPES.SEARCH : VIEW_TYPES.HEADINGS);

  useEffect(() => {
    // Reset to search view when opening the link editor
    if (bubbleViewState === 'linkEditor') {
      setView(VIEW_TYPES.SEARCH);
    }
  }, [bubbleViewState]);

  if (!editor || (bubbleViewState !== 'linkEditor' && !isPreview)) {
    return null;
  }

  if (isPreview) {
    return <LinkPreview />;
  }

  return (
    <BaseBubble avatar={false}>
      {view === VIEW_TYPES.SEARCH && <LinkAssetSearch />}
      <ViewToggleButton
        view={view}
        onToggle={toggleView}
        editor={editor}
      />
      {view === VIEW_TYPES.HEADINGS && <LinkHeadingsList />}
    </BaseBubble>
  );
};
